import { createEmployeeSchema, type EmployeeDetails } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Button, Field, Input, Select } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { departmentsTreeQuery, flattenTree, positionsQuery } from '@/lib/queries';

/** Форма создания и редактирования сотрудника. Проверяется той же схемой, что и на сервере. */
export function EmployeeForm({
  employee,
  onDone,
}: {
  employee?: EmployeeDetails;
  onDone: (id?: string) => void;
}) {
  const qc = useQueryClient();
  const tree = useQuery(departmentsTreeQuery);
  const positions = useQuery(positionsQuery);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      employee
        ? api<{ id: string }>(`/org/employees/${employee.id}`, { method: 'PATCH', body })
        : api<{ id: string }>('/org/employees', { method: 'POST', body }),
    onSuccess: async (row) => {
      await qc.invalidateQueries({ queryKey: ['org'] });
      onDone(row.id);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length ? null : err.message);
      }
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const raw = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    const data = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, v === '' ? null : v]));
    const parsed = createEmployeeSchema.safeParse(data);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <form onSubmit={onSubmit} noValidate className="px-5 py-5">
      <p className="mb-4 font-semibold">{employee ? 'Изменение данных' : 'Новый сотрудник'}</p>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Фамилия" error={errors.lastName}>
          {(p) => <Input {...p} name="lastName" defaultValue={employee?.lastName} autoFocus />}
        </Field>
        <Field label="Имя" error={errors.firstName}>
          {(p) => <Input {...p} name="firstName" defaultValue={employee?.firstName} />}
        </Field>
        <Field label="Отчество" error={errors.middleName}>
          {(p) => <Input {...p} name="middleName" defaultValue={employee?.middleName ?? ''} />}
        </Field>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Отдел" error={errors.departmentId}>
          {(p) => (
            <Select {...p} name="departmentId" defaultValue={employee?.departmentId ?? ''}>
              <option value="">Выберите отдел</option>
              {tree.data &&
                flattenTree(tree.data).map(({ node, depth }) => (
                  <option key={node.id} value={node.id}>
                    {' '.repeat(depth)}
                    {node.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Field label="Должность" error={errors.positionId}>
          {(p) => (
            <Select {...p} name="positionId" defaultValue={employee?.positionId ?? ''}>
              <option value="">Не указана</option>
              {positions.data?.map((pos) => (
                <option key={pos.id} value={pos.id}>
                  {pos.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Телефон" error={errors.phone}>
          {(p) => (
            <Input
              {...p}
              name="phone"
              type="tel"
              defaultValue={employee?.phone ?? ''}
              placeholder="+7 900 000-00-00"
            />
          )}
        </Field>
        <Field label="Email" error={errors.email}>
          {(p) => <Input {...p} name="email" type="email" defaultValue={employee?.email ?? ''} />}
        </Field>
        <Field label="Дата приёма" error={errors.hireDate}>
          {(p) => <Input {...p} name="hireDate" type="date" defaultValue={employee?.hireDate ?? ''} />}
        </Field>
      </div>
      {formError && (
        <p role="alert" className="mt-4 rounded-md bg-danger-soft px-3 py-2 text-[13.5px] text-danger">
          {formError}
        </p>
      )}
      <div className="mt-5 flex gap-2">
        <Button type="submit" loading={save.isPending}>
          {employee ? 'Сохранить' : 'Добавить сотрудника'}
        </Button>
        <Button variant="ghost" onClick={() => onDone()}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
